import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Crown, Loader2 } from "lucide-react";
import { API, apiFetch } from "@/lib/gp";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [planState, setPlanState] = useState("loading");
  const [planError, setPlanError] = useState("");
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, refreshUser } = useAuth();

  // The plan list is served by the live API. Never fall back to invented prices:
  // if the list cannot be loaded the page says so instead of rendering cards.
  const loadPlans = useCallback(async () => {
    setPlanState("loading");
    setPlanError("");
    try {
      const res = await fetch(`${API}/plans`, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`the plans API answered ${res.status}`);
      const data = await res.json().catch(() => null);
      if (!data || !Array.isArray(data.plans)) throw new Error("the plans API did not return a plan list");
      if (data.plans.length === 0) throw new Error("the plans API returned an empty plan list");
      setPlans(data.plans);
      setPlanState("ready");
    } catch (e) {
      setPlans([]);
      setPlanError(e?.message || "the plans API could not be reached");
      setPlanState("error");
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    const checkoutId = params.get("checkout_id");
    if (!checkoutId || !user) return undefined;

    let stopped = false;
    let attempts = 0;
    const verify = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const res = await apiFetch(`/checkout/status/${encodeURIComponent(checkoutId)}`);
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.status === "active") {
          stopped = true;
          toast.success("Monthly subscription confirmed — your plan is active!");
          await (refreshUser && refreshUser());
          window.history.replaceState(null, "", "/pricing");
          return;
        }
      } catch {
        // Verification can lag briefly behind Square's redirect; polling below retries safely.
      }
      if (!stopped && attempts < 12) window.setTimeout(verify, 2500);
      else if (!stopped) toast.info("Square is still confirming the subscription. Refresh this page in a moment.");
    };
    verify();
    return () => { stopped = true; };
  }, [params, user, refreshUser]);

  const subscribe = async (plan) => {
    if (plan.available_for_purchase === false) return;
    if (plan.amount === 0) { navigate("/chat"); return; }
    if (!user) {
      toast.info("Create a free account first so your plan follows you everywhere.");
      navigate("/login", { state: { from: "/pricing" } });
      return;
    }
    setBusy(plan.id);
    try {
      const res = await apiFetch("/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan_id: plan.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Checkout unavailable");
      if (data.url) window.location.assign(data.url);
      else toast.info(data.message || "Try again shortly.");
    } catch (e) {
      toast.error(String(e.message || e).replace(/^Error:\s*/, ""));
    } finally {
      setBusy(null);
    }
  };

  const paid = Boolean(user && user.plan && user.plan !== "rookie");

  const cancelSubscription = async () => {
    if (!paid) return;
    if (!window.confirm("Cancel your GamePlat subscription at the end of the current billing period?")) return;
    setBusy("cancel");
    try {
      const res = await apiFetch("/billing/cancel", { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Cancellation unavailable");
      toast.success(data.cancel_at ? `Cancellation scheduled for ${data.cancel_at}.` : "Cancellation scheduled with Square.");
      await (refreshUser && refreshUser());
    } catch (e) {
      toast.error(String(e.message || e).replace(/^Error:\s*/, ""));
    } finally {
      setBusy(null);
    }
  };

  const refundLastPayment = async () => {
    if (!paid) return;
    if (!window.confirm("Refund the last Square payment and return this account to Rookie immediately? This cannot be undone.")) return;
    setBusy("refund");
    try {
      const res = await apiFetch("/billing/refund", { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Refund unavailable");
      toast.success("Refund submitted. Your plan is back to Rookie.");
      await (refreshUser && refreshUser());
    } catch (e) {
      toast.error(String(e.message || e).replace(/^Error:\s*/, ""));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-20">
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="font-unbounded font-black tracking-tighter text-4xl sm:text-5xl">Plans built to win</h1>
        <p className="text-white/65 mt-4 text-lg">Start free. Upgrade when you're ready to dominate every game on the platform.</p>
        <p className="text-white/45 mt-3 text-xs">Paid plans renew monthly until canceled. Review the <Link to="/terms" className="underline">Terms</Link> and <Link to="/refunds" className="underline">cancellation/refund notice</Link> before checkout.</p>
      </div>

      {planState === "loading" && (
        <div className="mt-14 flex items-center justify-center gap-3 py-16 text-white/60" data-testid="plans-loading">
          <Loader2 className="animate-spin text-gp-blue" size={20} /> Loading the plan list…
        </div>
      )}

      {planState === "error" && (
        <div className="mt-14 glass rounded-3xl p-8 md:p-10 text-center" data-testid="plans-error" role="alert">
          <p className="font-vt323 uppercase tracking-widest text-gp-gold text-sm">Plans</p>
          <h2 className="font-unbounded font-bold text-2xl md:text-3xl mt-1">Not working yet: the plan cards could not be loaded</h2>
          <p className="text-white/65 mt-4 max-w-2xl mx-auto text-sm leading-relaxed">
            The plan list is served by the live API (<code className="text-gp-blue">{API}/plans</code>). That request did not complete:{" "}
            <span className="text-white/85">{planError}</span>. No prices are shown here because the API returned none — nothing is estimated or cached.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Button onClick={loadPlans} data-testid="plans-retry" className="rounded-full bg-gp-blue font-bold px-6">
              Retry loading plans
            </Button>
            <Link to="/support" className="text-white/60 hover:text-white text-sm self-center">Report the API problem to support</Link>
          </div>
        </div>
      )}

      {planState === "ready" && (
        <div className="mt-14 grid md:grid-cols-3 gap-6 items-stretch">
          {plans.map((p) => (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`relative rounded-3xl p-8 flex flex-col ring-1 transition-transform hover:-translate-y-1 ${
                p.highlight
                  ? "bg-gp-surface ring-gp-gold/60 shadow-[0_0_40px_rgba(240,192,64,0.18)]"
                  : "glass ring-white/10"
              }`}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-gp-gold px-4 py-1 text-xs font-bold text-gp-bg">
                  <Crown size={13} /> Most popular
                </span>
              )}
              <h3 className="font-unbounded font-bold text-2xl">{p.name}</h3>
              <p className="text-white/60 text-sm mt-1 min-h-[40px]">{p.tagline}</p>
              <div className="mt-5 flex items-end gap-1">
                <span className="font-unbounded font-black text-5xl tracking-tighter">${p.price}</span>
                <span className="text-white/50 mb-1">/{p.cadence}</span>
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {(p.features || []).map((f) => (
                  <li key={f} className="flex gap-2.5 text-sm text-white/80">
                    <Check size={17} className={p.highlight ? "text-gp-gold shrink-0 mt-0.5" : "text-gp-blue shrink-0 mt-0.5"} />
                    {f}
                  </li>
                ))}
              </ul>
              {p.availability_note && <p className="mt-4 text-sm text-white/60">{p.availability_note}</p>}
              <Button
                data-testid={`subscribe-${p.id}`}
                onClick={() => subscribe(p)}
                disabled={busy === p.id || p.available_for_purchase === false || (user && user.plan === p.id)}
                className={`mt-8 rounded-full py-6 font-bold transition-transform hover:-translate-y-0.5 ${
                  p.highlight
                    ? "bg-gp-gold hover:bg-gp-gold/90 text-gp-bg shadow-[0_0_22px_rgba(240,192,64,0.4)]"
                    : "bg-gp-blue hover:bg-gp-blue/90 text-white"
                }`}
              >
                {busy === p.id ? <Loader2 className="animate-spin" size={18} /> : user && user.plan === p.id ? "Current plan" : p.available_for_purchase === false ? "Not working yet" : p.amount === 0 ? "Start free" : `Get ${p.name}`}
              </Button>
            </div>
          ))}
        </div>
      )}

      <p className="text-center text-white/40 text-sm mt-10">
        Secure recurring billing via Square. Paid plans renew monthly until cancelled. Prices in USD.
      </p>
      {paid && (
        <div className="text-center mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            variant="ghost"
            disabled={busy === "cancel" || Boolean(user.subscription_cancel_at)}
            onClick={cancelSubscription}
            className="text-white/55 hover:text-white"
          >
            {user.subscription_cancel_at ? `Cancellation scheduled: ${user.subscription_cancel_at}` : busy === "cancel" ? <Loader2 className="animate-spin" size={18} /> : "Cancel at period end"}
          </Button>
          <Button
            variant="ghost"
            data-testid="refund-last-payment"
            disabled={busy === "refund"}
            onClick={refundLastPayment}
            className="text-white/55 hover:text-white"
          >
            {busy === "refund" ? <Loader2 className="animate-spin" size={18} /> : "Refund last payment"}
          </Button>
        </div>
      )}
    </div>
  );
}
